import React from 'react';
import { QuickFilter, SortField, SortDirection } from '../../types/budget';
import { useBudget } from './BudgetContext';
import { Button } from '../ui/Button';
import styles from './FilterBar.module.css';

const QUICK_FILTERS: { value: QuickFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'uncategorized', label: 'Uncategorized' },
  { value: 'expenses', label: 'Expenses' },
  { value: 'income', label: 'Income' },
];

const SORT_OPTIONS: { field: SortField; direction: SortDirection; label: string }[] = [
  { field: 'date', direction: 'desc', label: 'Date (newest)' },
  { field: 'date', direction: 'asc', label: 'Date (oldest)' },
  { field: 'amount', direction: 'desc', label: 'Amount (highest)' },
  { field: 'amount', direction: 'asc', label: 'Amount (lowest)' },
  { field: 'description', direction: 'asc', label: 'Description (A-Z)' },
  { field: 'description', direction: 'desc', label: 'Description (Z-A)' },
];

export const FilterBar: React.FC = () => {
  const {
    categories,
    activeAccounts,
    filters,
    setQuickFilter,
    setCategoryFilter,
    setAccountFilter,
    setSearchQuery,
    setDateRange,
    setSort,
    resetFilters,
  } = useBudget();

  const { quickFilter, categoryId, accountId, searchQuery, dateFrom, dateTo, sortField, sortDirection } = filters;
  const hasActiveFilters = categoryId || accountId || searchQuery || dateFrom || dateTo || quickFilter !== 'uncategorized';

  const handleSortChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const [field, direction] = e.target.value.split('-') as [SortField, SortDirection];
    setSort(field, direction);
  };

  return (
    <div className={styles.filterBar}>
      {/* Quick filter buttons */}
      <div className={styles.quickFilters}>
        {QUICK_FILTERS.map((filter) => (
          <Button
            key={filter.value}
            variant={quickFilter === filter.value ? 'primary' : 'ghost'}
            size="sm"
            onClick={() => setQuickFilter(filter.value)}
            aria-pressed={quickFilter === filter.value}
          >
            {filter.label}
          </Button>
        ))}
      </div>

      <div className={styles.filterControls}>
        {/* Search input */}
        <input
          type="text"
          className={styles.searchInput}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search description or amount..."
          aria-label="Search transactions"
        />

        {/* Category dropdown */}
        <select
          className={styles.categorySelect}
          value={categoryId || ''}
          onChange={(e) => setCategoryFilter(e.target.value || null)}
          aria-label="Filter by category"
        >
          <option value="">All Categories</option>
          {categories
            .filter(c => c.type === 'expense')
            .map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.name}
              </option>
            ))}
          {categories
            .filter(c => c.type === 'income')
            .map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.name}
              </option>
            ))}
          {categories
            .filter(c => c.type === 'transfer')
            .map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.name}
              </option>
            ))}
        </select>

        {/* Account dropdown */}
        <select
          className={styles.accountSelect}
          value={accountId || ''}
          onChange={(e) => setAccountFilter(e.target.value || null)}
          aria-label="Filter by account"
        >
          <option value="">All Accounts</option>
          {activeAccounts.map((account) => (
            <option key={account.id} value={account.id}>
              {account.name}
            </option>
          ))}
        </select>

        {/* Date range */}
        <div className={styles.dateRange}>
          <input
            type="date"
            className={styles.dateInput}
            value={dateFrom || ''}
            onChange={(e) => setDateRange(e.target.value || null, dateTo)}
            placeholder="From"
            aria-label="Filter from date"
          />
          <span className={styles.dateSeparator}>to</span>
          <input
            type="date"
            className={styles.dateInput}
            value={dateTo || ''}
            onChange={(e) => setDateRange(dateFrom, e.target.value || null)}
            placeholder="To"
            aria-label="Filter to date"
          />
        </div>

        {/* Sort dropdown */}
        <select
          className={styles.sortSelect}
          value={`${sortField}-${sortDirection}`}
          onChange={handleSortChange}
          aria-label="Sort transactions"
        >
          {SORT_OPTIONS.map((option) => (
            <option key={`${option.field}-${option.direction}`} value={`${option.field}-${option.direction}`}>
              {option.label}
            </option>
          ))}
        </select>

        {/* Clear filters */}
        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={resetFilters}
            aria-label="Clear all filters"
          >
            Clear
          </Button>
        )}
      </div>
    </div>
  );
};

export default FilterBar;
